import SEOHead from '@/components/SEOHead';
import { Col, Row } from 'antd';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import toast from 'react-hot-toast';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    const toastId = toast.loading('Sending your message...');

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'contact',
          ...formData,
        }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Message sent successfully!', { id: toastId });
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: '',
        });
      } else {
        toast.error(result.message || 'Failed to send message', { id: toastId });
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.', { id: toastId });
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <SEOHead />
      <section className="section hero v9">
        <div className="container-default w-container">
          <div className="mg-bottom-80px">
            <div className="inner-container _662px center">
              <div className="text-center">
                <div className="inner-container _500px---mbl center">
                  <div className="mg-bottom-24px">
                    <div className="text-100 bold color-accent-1 text-uppercase">Contact Us</div>
                  </div>
                  <h1 className="display-1 color-neutral-100 mg-bottom-16px">
                    Get in touch with our team today
                  </h1>
                </div>
                <p className="color-neutral-300 mg-bottom-0">
                  We're here to assist you with any questions or concerns you may have. Don't
                  hesitate to reach out—your satisfaction is our top priority.
                </p>
              </div>
            </div>
          </div>
          <Row gutter={[48, 24]}>
            <Col xs={24} md={16} lg={18}>
              <div className="position-relative">
                <div className="card form-v3 w-form">
                  <form
                    id="wf-form-Contact-Form"
                    name="wf-form-Contact-Form"
                    data-name="Contact Form"
                    method="post"
                    onSubmit={handleSubmit}
                  >
                    <div className="w-layout-grid grid-2-columns form">
                      <div>
                        <label htmlFor="name" className="color-neutral-100">
                          Name
                        </label>
                        <input
                          type="text"
                          className="input dark w-input"
                          maxLength="256"
                          name="name"
                          placeholder="Enter full name"
                          id="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-v3-email" className="color-neutral-100">
                          Email
                        </label>
                        <input
                          type="email"
                          className="input dark w-input"
                          maxLength="256"
                          name="email"
                          placeholder="Enter email address"
                          id="contact-v3-email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="color-neutral-100">
                          Phone
                        </label>
                        <input
                          type="tel"
                          className="input dark w-input"
                          maxLength="256"
                          name="phone"
                          placeholder="Enter phone number"
                          id="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="company" className="color-neutral-100">
                          Company
                        </label>
                        <input
                          type="text"
                          className="input dark w-input"
                          maxLength="256"
                          name="company"
                          placeholder="Enter company name"
                          id="company"
                          value={formData.company}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="message-wrapper">
                        <label htmlFor="message" className="color-neutral-100">
                          Leave us a message
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          maxLength="2000"
                          placeholder="Please type your message here..."
                          className="text-area dark w-input full-width"
                          style={{ height: '120px' }}
                          value={formData.message}
                          onChange={handleChange}
                          required
                          data-fullwidth="true"
                        ></textarea>
                      </div>
                      <div
                        className="buttons-row"
                        style={{
                          justifyContent: 'flex-end',
                          marginTop: '24px',
                          gridColumn: '1 / -1',
                        }}
                      >
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="btn-primary w-button"
                        >
                          {isSubmitting ? 'Please wait...' : 'Send Message'}
                          <span className="line-rounded-icon link-icon-right"></span>
                        </button>
                      </div>
                    </div>
                  </form>
                  <div className="success-message w-form-done">
                    <div>
                      <div className="line-rounded-icon success-message-check large"></div>
                      <div className="color-neutral-100">
                        Your message has been submitted. <br />
                        We will get back to you within 24-48 hours.
                      </div>
                    </div>
                  </div>
                  <div className="error-message w-form-fail">
                    <div>Oops! Something went wrong.</div>
                  </div>
                </div>
                <div className="position-absolute card-form-v3-bg"></div>
              </div>
            </Col>
            <Col xs={24} md={8} lg={6}>
              <div className="inner-container _400px---mbl center">
                <div className="contact-links-v2-wrapper">
                  <div className="contact-link-wrapper">
                    <div className="mg-bottom-16px">
                      <div className="inner-container _46px center---tablet">
                        <div className="image-wrapper">
                          <Image
                            src="/images/icon-3-contact-links-techkit-template.svg"
                            loading="eager"
                            alt="Email - Brandymail.com"
                            className="image cover"
                            width={100}
                            height={100}
                          />
                        </div>
                      </div>
                    </div>
                    <h2 className="display-4 color-neutral-100 mg-bottom-12px">Send us an email</h2>
                    <p className="color-neutral-200 mg-bottom-34px">
                      We're here to assist you with any inquiries. Drop us a message, and we'll get
                      back to you promptly.
                    </p>
                    <Link
                      href="mailto:support@brandymail.com"
                      className="link-wrapper color-primary w-inline-block"
                    >
                      <div className="link-text">support@brandymail.com</div>
                      <div className="line-rounded-icon link-icon-right"></div>
                    </Link>
                  </div>
                  <div className="divider contact-links-v2-divider"></div>
                  <div className="contact-link-wrapper">
                    <div className="mg-bottom-16px">
                      <div className="inner-container _46px center---tablet">
                        <div className="image-wrapper">
                          <Image
                            src="/images/icon-4-contact-links-techkit-template.svg"
                            loading="eager"
                            alt="Phone - Brandymail.com"
                            className="image cover"
                            width={100}
                            height={100}
                          />
                        </div>
                      </div>
                    </div>
                    <h2 className="display-4 color-neutral-100 mg-bottom-12px">Give us a call</h2>
                    <p className="color-neutral-200 mg-bottom-34px">
                      Need immediate assistance? Give us a call, and our team will be happy to help.
                    </p>
                    <Link
                      href="tel:(813)848-0518"
                      className="link-wrapper color-primary w-inline-block"
                    >
                      <div className="link-text">(813) 848-0518</div>
                      <div className="line-rounded-icon link-icon-right"></div>
                    </Link>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </section>
    </div>
  );
};

export default Contact;
